import { BaseApiService } from './BaseApiService';

export interface ProcessedInvoiceItem {
  id: number;
  do_sop_number: string;
  do_item_number: string;
  do_quantity_sold: number;
  do_unit_price?: number;
  do_extended_price?: number;
  do_invoice_date: string;
  processed_at: string;
  processing_status:
    | 'pending'
    | 'processing'
    | 'completed'
    | 'failed'
    | 'unable_to_process'
    | 'processed_with_errors'
    | 'pending_production';
  inventory_adjustment_id?: number;
  production_order_id?: number;
  error_message?: string;
  created_by?: number;
  created_at: string;
  updated_at: string;
  created_by_user?: {
    id: number;
    username: string;
    email: string;
  };
  product_name?: string; // Product name from products table
}

export interface ProcessingStats {
  total_invoice_items: number;
  processed_invoice_items: number;
  inventory_adjustments: number;
  production_orders_created: number;
  errors: number;
  products_not_found: number;
  processed_with_errors: number;
  totalProcessed: number;
  successRate: number;
}

export interface InvoiceItemFilters {
  do_sop_number?: string;
  do_item_number?: string;
  processing_status?: string;
  do_invoice_date_from?: string;
  do_invoice_date_to?: string;
  page?: number;
  pageSize?: number;
}

export interface ProcessedInvoiceItemsResponse {
  items: ProcessedInvoiceItem[];
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export interface UnprocessedInvoice {
  do_sop_number: string;
  do_item_number: string;
  do_item_description: string;
  do_quantity: number;
  do_unit_price: number;
  do_extended_price: number;
  do_invoice_date: string;
  product_id?: number;
}

export interface ProcessInvoiceRequest {
  startDate: string;
  endDate: string;
  dryRun?: boolean;
}

class InvoiceProcessingService extends BaseApiService {
  private baseUrl = '/invoice-processing';

  /**
   * Get processing statistics for a date range
   */
  async getProcessingStats(startDate: string, endDate: string): Promise<ProcessingStats> {
    const response = await this.request<{ success: boolean; data: ProcessingStats }>(
      `${this.baseUrl}/stats?startDate=${startDate}&endDate=${endDate}`
    );
    return response.data;
  }

  /**
   * Step 1: Find and import unprocessed invoices from DashboardObjs tables
   */
  async findUnprocessedInvoices(
    request: ProcessInvoiceRequest
  ): Promise<{ success: boolean; imported: number; errors: string[] }> {
    return this.post(`${this.baseUrl}/find-unprocessed`, request);
  }

  /**
   * Step 2: Process pending invoice items
   */
  async processPendingInvoices(
    dryRun: boolean = false
  ): Promise<{ success: boolean; processed: number; errors: number; results: any[] }> {
    return this.post(`${this.baseUrl}/process-pending`, { dryRun });
  }

  /**
   * Get pending invoice items count
   */
  async getPendingCount(): Promise<{ pendingCount: number }> {
    const response = await this.request<{ success: boolean; data: { pendingCount: number } }>(
      `${this.baseUrl}/pending-count`
    );
    return response.data;
  }

  /**
   * Get processed invoice items with filtering and pagination
   */
  async getProcessedInvoiceItems(
    filters: InvoiceItemFilters = {}
  ): Promise<ProcessedInvoiceItemsResponse> {
    const queryParams = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const url = queryString
      ? `${this.baseUrl}/processed-items?${queryString}`
      : `${this.baseUrl}/processed-items`;

    const response = await this.request<{ success: boolean; data: ProcessedInvoiceItemsResponse }>(
      url
    );
    return response.data;
  }

  /**
   * Get a specific processed invoice item by ID
   */
  async getProcessedInvoiceItem(id: number): Promise<ProcessedInvoiceItem> {
    const response = await this.request<{ success: boolean; data: ProcessedInvoiceItem }>(
      `${this.baseUrl}/processed-items/${id}`
    );
    return response.data;
  }

  /**
   * Get status summary for a date range
   */
  async getStatusSummary(startDate: string, endDate: string): Promise<ProcessingStats> {
    return this.request<ProcessingStats>(
      `${this.baseUrl}/stats?startDate=${startDate}&endDate=${endDate}`
    );
  }

  /**
   * Process a single invoice item by item number (and optionally SOP number)
   */
  async processItem(
    do_item_number: string,
    do_sop_number?: string,
    dryRun: boolean = false
  ): Promise<{
    success: boolean;
    data: {
      processed_invoice_item: ProcessedInvoiceItem;
      inventory_adjustment?: any;
      production_order?: any;
      emergency_production_order?: any;
      regular_production_order?: any;
      errors: string[];
      preview?: {
        currentStock: {
          cases: number;
          units: number;
          totalUnits: number;
        };
        toDeduct: {
          cases: number;
          units: number;
          totalUnits: number;
        };
        wouldResultIn: {
          cases: number;
          units: number;
          totalUnits: number;
        };
        unitsPerCase: number;
        inventoryBatches: Array<{
          id: number;
          casesAvailable: number;
          unitsAvailable: number;
          expiryDate: string;
        }>;
        insufficientInventory?: boolean;
        shortage?: {
          cases: number;
          units: number;
          totalUnits: number;
        };
      };
    };
  }> {
    return this.post(`${this.baseUrl}/process-item`, {
      do_item_number,
      do_sop_number,
      dryRun,
    });
  }
}

export const invoiceProcessingService = new InvoiceProcessingService();
